<template>
  <div class="comando">
    <div ref="mapa" class="comando__mapa"></div>

    <aside class="comando__panel" :class="{ 'comando__panel--min': minimizado }">
      <header class="panel__cabecera">
        <div>
          <h1>Comando</h1>
          <p>Dicta una consulta y el globo dibuja la capa KMZ más cercana.</p>
        </div>
        <div class="panel__acciones">
          <span v-if="usuario" class="panel__usuario" :title="usuario.nombre">
            <i class="mdi mdi-account-circle"></i> {{ usuario.nombre }}
          </span>
          <button
            v-if="usuario"
            class="panel__salir"
            type="button"
            title="Cerrar sesión"
            aria-label="Cerrar sesión"
            @click="salir"
          >
            <i class="mdi mdi-logout"></i>
          </button>
          <button class="panel__plegar" type="button" @click="minimizado = !minimizado">
            <i class="mdi" :class="minimizado ? 'mdi-chevron-down' : 'mdi-chevron-up'"></i>
          </button>
        </div>
      </header>

      <div v-show="!minimizado" class="panel__cuerpo">
        <div class="tipos" role="group" aria-label="Tipo de terreno">
          <button
            v-for="t in tipos"
            :key="t.nombre"
            class="tipos__boton"
            :class="{ 'tipos__boton--activo': tipo === t.clave }"
            type="button"
            :disabled="procesando"
            :aria-pressed="tipo === t.clave"
            @click="tipo = t.clave"
          >
            <i class="mdi" :class="t.icono"></i> {{ t.nombre }}
          </button>
        </div>

        <voice-recorder :ocupado="procesando" @grabado="procesar" @error="fallar" @inicio="reiniciar" />

        <p v-if="mensaje" class="panel__aviso" :class="`panel__aviso--${tipoMensaje}`">
          {{ mensaje }}
        </p>

        <ol v-if="consulta" class="pasos">
          <li
            v-for="paso in pasos"
            :key="paso.clave"
            class="pasos__item"
            :class="`pasos__item--${paso.estado}`"
          >
            <span class="pasos__punto">
              <i class="mdi" :class="paso.icono"></i>
            </span>
            <span class="pasos__texto">
              <strong>
                {{ paso.titulo }}
                <em v-if="paso.segundos != null" class="pasos__tiempo">{{ paso.segundos }} s</em>
              </strong>
              <small v-if="paso.detalle">{{ paso.detalle }}</small>
            </span>
          </li>
          <li v-if="tiempoTotal != null" class="pasos__total">
            <span class="pasos__texto">
              <strong>Total <em class="pasos__tiempo">{{ tiempoTotal }} s</em></strong>
            </span>
          </li>
        </ol>

        <section v-if="consulta && consulta.transcripcion" class="tarjeta">
          <h2><i class="mdi mdi-text-recognition"></i> Transcripción</h2>
          <p class="tarjeta__cita">“{{ consulta.transcripcion }}”</p>
        </section>

        <section v-if="consulta && consulta.sql_generado" class="tarjeta">
          <h2><i class="mdi mdi-database-search"></i> Consulta del agente</h2>
          <p v-if="consulta.agente_notas" class="tarjeta__nota">{{ consulta.agente_notas }}</p>
          <pre class="tarjeta__sql">{{ consulta.sql_generado }}</pre>
        </section>

        <section v-if="capa" class="tarjeta tarjeta--capa">
          <h2><i class="mdi mdi-map-marker-radius"></i> {{ capa.nombre }}</h2>
          <p class="tarjeta__nota">{{ capa.direccion }}</p>
          <dl class="ficha">
            <div><dt>Comuna</dt><dd>{{ capa.comuna || '—' }}</dd></div>
            <div><dt>Región</dt><dd>{{ capa.region || '—' }}</dd></div>
            <div><dt>Superficie</dt><dd>{{ metros(capa.superficie_m2) }}</dd></div>
            <div><dt>Valor</dt><dd>{{ uf(capa.valor_uf_m2) }} UF/m²</dd></div>
            <div><dt>Valor total</dt><dd>{{ uf(capa.valor_uf_total) }} UF</dd></div>
            <div><dt>Densidad máx.</dt><dd>{{ densidad(capa) }}</dd></div>
            <div><dt>Altura máx.</dt><dd>{{ altura(capa) }}</dd></div>
            <div><dt>Construcción máx.</dt><dd>{{ construccion(capa) }}</dd></div>
            <div><dt>Zonificación</dt><dd>{{ zonificacion(capa) }}</dd></div>
            <div><dt>Rol</dt><dd>{{ capa.rol || '—' }}</dd></div>
          </dl>
          <div v-if="usos(capa).length" class="etiquetas-bloque">
            <span class="etiquetas-bloque__titulo">Usos permitidos</span>
            <ul class="etiquetas">
              <li v-for="uso in usos(capa)" :key="uso">{{ uso }}</li>
            </ul>
          </div>
          <button class="tarjeta__accion" type="button" @click="repetirVuelo">
            <i class="mdi mdi-airplane-takeoff"></i> Repetir acercamiento
          </button>
        </section>

      </div>
    </aside>

    <!-- Controles del globo: mapa base y rotación. -->
    <div class="comando__controles">
      <div class="controles__grupo">
        <button
          v-for="base in mapasBase"
          :key="base.clave"
          class="controles__boton"
          :class="{ 'controles__boton--activo': mapaBase === base.clave }"
          type="button"
          :title="`Mapa ${base.nombre.toLowerCase()}`"
          @click="cambiarMapaBase(base.clave)"
        >
          <i class="mdi" :class="base.icono"></i>
        </button>
      </div>
      <button
        class="controles__boton controles__boton--suelto"
        :class="{ 'controles__boton--activo': girando }"
        type="button"
        :title="girando ? 'Pausar rotación' : 'Rotar globo'"
        @click="alternarRotacion"
      >
        <i class="mdi" :class="girando ? 'mdi-pause' : 'mdi-rotate-3d-variant'"></i>
      </button>
    </div>

    <div v-if="animando" class="comando__cinta">Acercando al área…</div>

    <!-- Infografía del terreno: aparece al pasar el mouse sobre el polígono. -->
    <div
      v-if="ficha.visible && ficha.datos"
      class="infografia"
      :style="{ left: `${ficha.x}px`, top: `${ficha.y}px` }"
    >
      <header class="infografia__cabecera">
        <span class="infografia__marca" :style="{ background: ficha.datos.color }"></span>
        <div>
          <strong>{{ ficha.datos.nombre }}</strong>
          <small>
            <i class="mdi mdi-map-marker"></i>
            {{ ficha.datos.comuna || '—' }}<template v-if="ficha.datos.region"> · {{ ficha.datos.region }}</template>
          </small>
        </div>
      </header>

      <table class="infografia__tabla">
        <tbody>
          <tr v-for="fila in filasFicha(ficha.datos)" :key="fila[0]">
            <th scope="row">{{ fila[0] }}</th>
            <td>{{ fila[1] }}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import VoiceRecorder from './VoiceRecorder.vue';
import {
  Caja,
  MAPAS_BASE,
  cargarCesium,
  configuracion,
  pausa,
  proveedorBase,
  recorrerCoordenadas,
} from '@/services/cesium';
import {
  cerrarSesion,
  enviarAudio,
  esperarResultado,
  obtenerConfig,
} from '@/services/comandoApi';

// El globo entero en pantalla: punto de partida y de vuelta del acercamiento.
const ALTURA_MUNDO = 18000000;
// Cada tramo del vuelo: primero se sale al globo, después se cae en vertical.
const VUELO_SALIDA = 2.5;
const VUELO_ENTRADA = 4.0;
// Velocidad de la rotación del globo mientras no hay nada dibujado (rad/s).
const VELOCIDAD_GIRO = 0.04;
// Piso del zoom propio de la rueda, en metros sobre el suelo.
const ALTURA_MINIMA = 150;
// Colores del polígono cuando el GeoJSON no trae estilo propio.
const COLOR_RELLENO = '#E53935';
const COLOR_BORDE = '#B71C1C';

// Los botones del panel; las claves son las de `usos.TIPOS` en el backend.
const TIPOS = [
  { clave: null, nombre: 'Todos', icono: 'mdi-earth' },
  { clave: 'habitacional', nombre: 'Habitacional', icono: 'mdi-home-city' },
  { clave: 'comercial', nombre: 'Comercial', icono: 'mdi-storefront' },
  { clave: 'industrial', nombre: 'Industrial', icono: 'mdi-factory' },
];

const PASOS = [
  { clave: 'subiendo', titulo: 'Audio a S3', icono: 'mdi-cloud-upload' },
  { clave: 'transcribiendo', titulo: 'Transcripción en Bedrock', icono: 'mdi-waveform' },
  { clave: 'razonando', titulo: 'Agente escribiendo el SQL', icono: 'mdi-robot' },
  { clave: 'buscando', titulo: 'Búsqueda en el catálogo KMZ', icono: 'mdi-database-search' },
  { clave: 'dibujando', titulo: 'Capa dibujada', icono: 'mdi-map-check' },
];

export default {
  name: 'CommandMap',
  components: { VoiceRecorder },
  data() {
    return {
      config: configuracion(),
      // Django lo deja en el HTML, así que ya está cuando el panel se dibuja.
      usuario: configuracion().usuario || null,
      mapasBase: MAPAS_BASE,
      mapaBase: 'satelital',
      girando: false,
      tipos: TIPOS,
      tipo: null,
      origen: null,
      consulta: null,
      capa: null,
      resultado: null,
      ficha: { visible: false, x: 0, y: 0, datos: null },
      mensaje: '',
      tipoMensaje: 'info',
      procesando: false,
      animando: false,
      minimizado: false,
    };
  },
  computed: {
    tiempoTotal() {
      const registro = (this.consulta && this.consulta.pasos) || [];
      if (registro.length < 2) return null;
      const inicio = new Date(registro[0].at).getTime();
      const fin = new Date(registro[registro.length - 1].at).getTime();
      if (Number.isNaN(inicio) || Number.isNaN(fin)) return null;
      return Math.round((fin - inicio) / 100) / 10;
    },

    pasos() {
      const estadoActual = this.consulta ? this.consulta.status : null;
      const indiceActual = PASOS.findIndex((p) => p.clave === estadoActual);
      const registro = (this.consulta && this.consulta.pasos) || [];
      const detalles = {};
      registro.forEach((p) => {
        if (p.detalle) detalles[p.status] = p.detalle;
      });

      // Cuánto duró cada etapa: de su primera marca a la del paso siguiente.
      // Las escribe el backend en `pasos[].at` cada vez que la consulta avanza.
      const segundos = {};
      registro.forEach((p, i) => {
        if (segundos[p.status] !== undefined) return;
        // El siguiente estado distinto, o la última marca si ya no hay: así el
        // paso final también muestra lo que tardó.
        const siguiente = registro.slice(i + 1).find((otro) => otro.status !== p.status)
          || registro[registro.length - 1];
        if (siguiente === p) return;
        const desde = new Date(p.at).getTime();
        const hasta = new Date(siguiente.at).getTime();
        if (!Number.isNaN(desde) && !Number.isNaN(hasta)) {
          segundos[p.status] = Math.round((hasta - desde) / 100) / 10;
        }
      });

      return PASOS.map((paso, indice) => {
        let estado = 'pendiente';
        if (estadoActual === 'listo') {
          estado = 'listo';
        } else if (estadoActual === 'error') {
          estado = indice < indiceActual || indiceActual === -1 ? 'listo' : 'pendiente';
        } else if (indiceActual === -1) {
          estado = 'pendiente';
        } else if (indice < indiceActual) {
          estado = 'listo';
        } else if (indice === indiceActual) {
          estado = estadoActual === 'listo' ? 'listo' : 'activo';
        }
        return {
          ...paso,
          estado,
          detalle: detalles[paso.clave] || '',
          segundos: segundos[paso.clave] != null ? segundos[paso.clave] : null,
        };
      });
    },
  },
  created() {
    // Fuera de `data` a propósito: el proxy reactivo de Vue los envuelve
    // enteros y el visor deja de funcionar.
    this.visor = null;
    this.capaBase = null;
    this.manejador = null;
    this.fuentes = [];
    this.realce = null;
    this.avanceColor = 1;
    this.marcadorOrigen = null;
    this.marcadores = [];
    this.ultimaCaja = null;
    this.centroGiro = null;
    this.rumbo = 0;
    this.ultimoGiro = 0;
  },
  async mounted() {
    try {
      const remota = await obtenerConfig();
      this.config = { ...this.config, ...remota };
      if (remota.mapa_base) this.mapaBase = remota.mapa_base;
      if (!remota.capas_activas) {
        this.avisar(
          'El catálogo no tiene capas activas. Corre: python manage.py seed_kmz',
          'alerta',
        );
      }
    } catch (e) {
      // Sin /api/config/ el mapa arranca con lo que dejó Django en el HTML.
    }

    try {
      await this.iniciarMapa();
    } catch (e) {
      this.fallar(e.message);
    }
  },
  beforeUnmount() {
    this.detenerRotacion();
    if (this.manejador && !this.manejador.isDestroyed()) this.manejador.destroy();
    if (this.visor && !this.visor.isDestroyed()) this.visor.destroy();
    this.visor = null;
  },
  methods: {
    // --- Sesión -------------------------------------------------------------
    async salir() {
      try {
        await cerrarSesion();
      } catch (e) {
        // Da lo mismo por qué: igual se va al login, que si la sesión sigue
        // viva redirige de vuelta al mapa.
      }
      window.location.assign('/login');
    },

    // --- Globo --------------------------------------------------------------
    async iniciarMapa() {
      const Cesium = await cargarCesium();
      const centro = this.config.centro || { lat: -33.4489, lng: -70.6693 };

      this.visor = new Cesium.Viewer(this.$refs.mapa, {
        animation: false,
        timeline: false,
        geocoder: false,
        homeButton: false,
        sceneModePicker: false,
        navigationHelpButton: false,
        baseLayerPicker: false,
        infoBox: false,
        selectionIndicator: false,
        fullscreenButton: false,
        baseLayer: new Cesium.ImageryLayer(proveedorBase(Cesium, this.mapaBase)),
      });
      this.capaBase = this.visor.imageryLayers.get(0);

      // El zoom propio reemplaza al de Cesium: con el de fábrica el trackpad
      // exige mantener el clic y el acercamiento sale a saltos.
      this.visor.scene.screenSpaceCameraController.enableZoom = false;

      this.centroGiro = { ...centro };
      this.visor.camera.setView({
        destination: Cesium.Cartesian3.fromDegrees(centro.lng, centro.lat, ALTURA_MUNDO),
        orientation: {
          heading: 0,
          pitch: Cesium.Math.toRadians(-90),
          roll: 0,
        },
      });

      this.origen = { ...centro };
      this.engancharInteraccion();
      this.iniciarRotacion();
    },

    engancharInteraccion() {
      const Cesium = window.Cesium;
      const lienzo = this.visor.scene.canvas;

      this.manejador = new Cesium.ScreenSpaceEventHandler(lienzo);

      // Hover: la infografía sigue al cursor y el polígono de abajo se realza.
      this.manejador.setInputAction((movimiento) => {
        const elegido = this.visor.scene.pick(movimiento.endPosition);
        const item = elegido && elegido.id ? elegido.id.comandoItem : null;

        if (!item) {
          this.ficha.visible = false;
          if (this.realce) {
            this.realce = null;
            this.aplicarEstilos(this.avanceColor);
          }
          return;
        }

        this.ficha.datos = item.datos;
        this.ficha.visible = true;
        this.posicionFicha(movimiento.endPosition);
        if (this.realce !== item) {
          this.realce = item;
          this.aplicarEstilos(this.avanceColor);
        }
      }, Cesium.ScreenSpaceEventType.MOUSE_MOVE);

      // Clic sobre el globo: fija el punto de referencia de la consulta.
      this.manejador.setInputAction((clic) => {
        const punto = this.visor.camera.pickEllipsoid(
          clic.position, this.visor.scene.globe.ellipsoid,
        );
        if (!punto) return;
        const grados = Cesium.Cartographic.fromCartesian(punto);
        this.fijarOrigen({
          lat: Cesium.Math.toDegrees(grados.latitude),
          lng: Cesium.Math.toDegrees(grados.longitude),
        }, 'clic en el mapa');
      }, Cesium.ScreenSpaceEventType.LEFT_CLICK);

      // Zoom suave con la rueda o el trackpad: el avance es proporcional a la
      // altura, así que cerca del suelo se mueve poco.
      lienzo.addEventListener('wheel', (evento) => {
        evento.preventDefault();
        this.detenerRotacion();
        const camara = this.visor.camera;
        const altura = camara.positionCartographic.height;
        const avance = -evento.deltaY * altura * 0.0015;
        // Sin el controlador de Cesium no hay topes: que la cámara no termine
        // bajo el suelo ni más lejos que el globo entero.
        const destino = altura - avance;
        if (destino < ALTURA_MINIMA || destino > ALTURA_MUNDO) return;
        camara.moveForward(avance);
      }, { passive: false });

      // Cualquier arrastre manda: si el usuario toma el globo, deja de girar.
      lienzo.addEventListener('pointerdown', () => this.detenerRotacion());
    },

    cambiarMapaBase(clave) {
      this.mapaBase = clave;
      if (!this.visor) return;
      const Cesium = window.Cesium;

      this.visor.imageryLayers.remove(this.capaBase);
      this.capaBase = new Cesium.ImageryLayer(proveedorBase(Cesium, clave));
      this.visor.imageryLayers.add(this.capaBase);
      this.visor.imageryLayers.lowerToBottom(this.capaBase);
    },

    // --- Rotación del globo -------------------------------------------------
    iniciarRotacion() {
      if (!this.visor || this.girando) return;
      this.girando = true;
      this.ultimoGiro = Date.now();
      this.visor.scene.postRender.addEventListener(this.rotar);
    },

    detenerRotacion() {
      if (!this.visor || !this.girando) return;
      this.girando = false;
      this.visor.scene.postRender.removeEventListener(this.rotar);
    },

    alternarRotacion() {
      if (this.girando) this.detenerRotacion();
      else this.iniciarRotacion();
    },

    rotar() {
      const Cesium = window.Cesium;
      const ahora = Date.now();
      this.rumbo += VELOCIDAD_GIRO * ((ahora - this.ultimoGiro) / 1000);
      this.ultimoGiro = ahora;

      this.visor.camera.setView({
        destination: Cesium.Cartesian3.fromDegrees(
          this.centroGiro.lng, this.centroGiro.lat, ALTURA_MUNDO,
        ),
        orientation: { heading: this.rumbo, pitch: Cesium.Math.toRadians(-90), roll: 0 },
      });
    },

    fijarOrigen(punto, motivo) {
      this.origen = punto;
      const Cesium = window.Cesium;
      if (!Cesium || !this.visor) return;

      if (this.marcadorOrigen) this.visor.entities.remove(this.marcadorOrigen);
      this.marcadorOrigen = this.visor.entities.add({
        name: `Punto de referencia (${motivo})`,
        position: Cesium.Cartesian3.fromDegrees(punto.lng, punto.lat),
        point: {
          pixelSize: 12,
          color: Cesium.Color.fromCssColorString('#40C4FF'),
          outlineColor: Cesium.Color.WHITE,
          outlineWidth: 2,
          heightReference: Cesium.HeightReference.CLAMP_TO_GROUND,
          disableDepthTestDistance: Number.POSITIVE_INFINITY,
        },
      });
    },

    // --- Pipeline -----------------------------------------------------------
    reiniciar() {
      this.mensaje = '';
      this.consulta = null;
      this.capa = null;
      this.resultado = null;
    },

    async procesar(blob, segundos) {
      this.procesando = true;
      this.avisar('Subiendo el audio…', 'info');
      try {
        const creada = await enviarAudio(blob, {
          lat: this.origen ? this.origen.lat : null,
          lng: this.origen ? this.origen.lng : null,
          segundos,
          tipoTerreno: this.tipo,
        });
        this.consulta = creada;
        this.avisar('', 'info');

        const lista = await esperarResultado(creada.id, {
          onAvance: (consulta) => { this.consulta = consulta; },
        });

        this.consulta = lista;
        this.resultado = lista.resultado || null;
        this.capa = this.resultado ? this.resultado.capa : null;

        const capas = (this.resultado && this.resultado.capas) || (this.capa ? [this.capa] : []);
        if (capas.length) await this.mostrarCapas(capas);
        else this.avisar('La consulta terminó sin terrenos que dibujar.', 'alerta');
      } catch (e) {
        this.fallar(e.message);
      } finally {
        this.procesando = false;
      }
    },

    // --- Dibujo -------------------------------------------------------------
    limpiarDibujo() {
      this.ficha.visible = false;
      this.realce = null;
      if (this.visor) {
        this.fuentes.forEach(({ fuente }) => this.visor.dataSources.remove(fuente, true));
        this.marcadores.forEach((m) => this.visor.entities.remove(m));
      }
      this.fuentes = [];
      this.marcadores = [];
    },

    // La infografía sigue al cursor y se corre si se sale por el borde. La
    // posición viene en coordenadas del lienzo: se le suma dónde está.
    posicionFicha(punto) {
      const ANCHO = 320;
      const ALTO = 420;
      const margen = 16;
      const caja = this.$refs.mapa.getBoundingClientRect();

      this.ficha.x = Math.min(caja.left + punto.x + 18, window.innerWidth - ANCHO - margen);
      this.ficha.y = Math.min(caja.top + punto.y + 18, window.innerHeight - ALTO - margen);
    },

    // Dibuja todos los terrenos que trajo la consulta y encuadra sobre el
    // conjunto.
    async mostrarCapas(capas) {
      const Cesium = window.Cesium;
      if (!this.visor) return;

      this.limpiarDibujo();

      const caja = new Caja();
      for (const capa of capas) {
        await this.dibujarCapa(capa, caja);
      }

      if (caja.vacia) {
        this.avisar('Los terrenos no traen geometría para dibujar.', 'alerta');
        return;
      }

      this.ultimaCaja = caja;
      this.aplicarEstilos(0);

      // Una etiqueta por terreno. Aparecen recién al aterrizar: durante el
      // vuelo sólo estorban.
      this.marcadores = capas.filter((c) => c.centro).map((c) => this.visor.entities.add({
        position: Cesium.Cartesian3.fromDegrees(c.centro.lng, c.centro.lat),
        label: {
          text: c.superficie_m2
            ? `${c.nombre}\n${Math.round(c.superficie_m2).toLocaleString('es-CL')} m²`
            : c.nombre,
          font: '13px Inter, Segoe UI, system-ui, sans-serif',
          fillColor: Cesium.Color.WHITE,
          outlineColor: Cesium.Color.fromCssColorString('#0e1720'),
          outlineWidth: 3,
          style: Cesium.LabelStyle.FILL_AND_OUTLINE,
          verticalOrigin: Cesium.VerticalOrigin.BOTTOM,
          pixelOffset: new Cesium.Cartesian2(0, -12),
          heightReference: Cesium.HeightReference.CLAMP_TO_GROUND,
          disableDepthTestDistance: Number.POSITIVE_INFINITY,
          show: false,
        },
      }));

      await this.volar(caja, caja.centro);
      this.marcadores.forEach((m) => { m.label.show = true; });
    },

    async dibujarCapa(capa, caja) {
      const Cesium = window.Cesium;
      const features = (capa.geojson && capa.geojson.features) || [];
      if (!features.length) return;

      // clampToGround: pegado al suelo se ve como una parcela y no como una
      // lámina flotando.
      const fuente = await Cesium.GeoJsonDataSource.load(capa.geojson, {
        clampToGround: true,
      });
      this.visor.dataSources.add(fuente);

      const registro = { fuente, capa, items: [] };
      this.fuentes.push(registro);

      fuente.entities.suspendEvents();
      fuente.entities.values.slice().forEach((entidad) => {
        const props = this.propiedades(entidad);
        const relleno = props.fill || COLOR_RELLENO;
        const borde = props.stroke || COLOR_BORDE;
        const item = {
          entidad,
          datos: this.datosFicha(capa, props, relleno),
          colorRelleno: Cesium.Color.fromCssColorString(relleno),
          colorBorde: Cesium.Color.fromCssColorString(borde),
          opacidad: props.fillOpacity != null ? props.fillOpacity : 0.35,
          ancho: props.strokeWidth || 2,
          contornos: [],
        };

        // Cesium no dibuja outlines sobre el terreno, así que el contorno de
        // un polígono pegado al suelo va como polilínea.
        if (entidad.polygon) {
          entidad.polygon.outline = false;
          const jerarquia = entidad.polygon.hierarchy.getValue(Cesium.JulianDate.now());
          const anillos = [jerarquia.positions, ...(jerarquia.holes || []).map((h) => h.positions)];
          anillos.filter((a) => a && a.length > 1).forEach((anillo) => {
            const cerrado = Cesium.Cartesian3.equals(anillo[0], anillo[anillo.length - 1]);
            const posiciones = cerrado ? anillo : anillo.concat([anillo[0]]);
            const contorno = fuente.entities.add({
              polyline: {
                positions: posiciones,
                width: item.ancho,
                clampToGround: true,
                material: item.colorBorde.withAlpha(0),
              },
            });
            contorno.comandoItem = item;
            item.contornos.push(contorno);
          });
        }

        // Los puntos del GeoJSON llegan como chincheta: se cambian por un
        // punto del color de la capa.
        if (entidad.billboard) {
          entidad.billboard = undefined;
          entidad.point = new Cesium.PointGraphics({
            pixelSize: 10,
            outlineColor: Cesium.Color.WHITE,
            outlineWidth: 2,
            heightReference: Cesium.HeightReference.CLAMP_TO_GROUND,
            disableDepthTestDistance: Number.POSITIVE_INFINITY,
          });
        }

        entidad.comandoItem = item;
        registro.items.push(item);
      });
      fuente.entities.resumeEvents();

      features.forEach((feature) => {
        recorrerCoordenadas(feature.geometry, (lng, lat) => caja.extender(lng, lat));
      });
    },

    // Cesium envuelve las propiedades del feature: esto las devuelve planas.
    propiedades(entidad) {
      const Cesium = window.Cesium;
      const salida = {};
      if (!entidad.properties) return salida;
      (entidad.properties.propertyNames || []).forEach((nombre) => {
        const valor = entidad.properties[nombre];
        salida[nombre] = valor && typeof valor.getValue === 'function'
          ? valor.getValue(Cesium.JulianDate.now())
          : valor;
      });
      return salida;
    },

    datosFicha(capa, props, color) {
      return {
        sector: props.name || '',
        color,
        nombre: capa.nombre,
        comuna: capa.comuna,
        region: capa.region,
        superficie: this.metros(capa.superficie_m2),
        valorM2: `${this.uf(capa.valor_uf_m2)} UF/m²`,
        valorTotal: `${this.uf(capa.valor_uf_total)} UF`,
        densidad: this.densidad(capa),
        alturaMax: this.altura(capa),
        construccion: this.construccion(capa),
        zonificacion: this.zonificacion(capa),
        usos: this.usos(capa),
      };
    },

    filasFicha(datos) {
      const filas = [
        ['Sector', datos.sector !== datos.nombre ? datos.sector : ''],
        ['Superficie', datos.superficie],
        ['Valor', datos.valorM2],
        ['Valor total', datos.valorTotal],
        ['Densidad máx.', datos.densidad],
        ['Altura máx.', datos.alturaMax],
        ['Construcción máx.', datos.construccion],
        ['Zonificación', datos.zonificacion],
        ['Usos permitidos', (datos.usos || []).join(' · ')],
      ];
      return filas.filter(([, valor]) => valor);
    },

    // `avance` es el fundido de entrada (0 a 1); el item bajo el cursor va
    // más marcado.
    aplicarEstilos(avance = 1) {
      this.avanceColor = avance;
      this.fuentes.forEach((registro) => {
        registro.items.forEach((item) => {
          const realzado = item === this.realce;
          const relleno = Math.min(1, item.opacidad + (realzado ? 0.22 : 0)) * avance;
          const borde = avance;

          if (item.entidad.polygon) {
            item.entidad.polygon.material = item.colorRelleno.withAlpha(relleno);
          }
          if (item.entidad.polyline) {
            item.entidad.polyline.material = item.colorBorde.withAlpha(borde);
            item.entidad.polyline.width = item.ancho + (realzado ? 2 : 0);
          }
          if (item.entidad.point) {
            item.entidad.point.color = item.colorRelleno.withAlpha(avance);
          }
          item.contornos.forEach((contorno) => {
            contorno.polyline.material = item.colorBorde.withAlpha(borde);
            contorno.polyline.width = item.ancho + (realzado ? 1.5 : 0);
          });
        });
      });
    },

    // Sale al globo entero y cae en vertical sobre el área: zoom-out y después
    // caída libre centrada.
    async volar(caja, centro) {
      const Cesium = window.Cesium;
      this.animando = true;
      this.detenerRotacion();
      this.centroGiro = { ...centro };

      try {
        await this.vuelo({
          destination: Cesium.Cartesian3.fromDegrees(centro.lng, centro.lat, ALTURA_MUNDO),
          orientation: { heading: 0, pitch: Cesium.Math.toRadians(-90), roll: 0 },
          duration: VUELO_SALIDA,
        });
        await pausa(400);
        await this.vuelo({
          destination: caja.rectangulo(Cesium),
          duration: VUELO_ENTRADA,
        });
        await this.aparecerColores();
      } finally {
        this.animando = false;
      }
    },

    vuelo(opciones) {
      return new Promise((resolve) => {
        this.visor.camera.flyTo({ ...opciones, complete: resolve, cancel: resolve });
      });
    },

    // Los polígonos entran con un fundido: al terminar, el área queda marcada.
    aparecerColores(duracion = 900) {
      return new Promise((resolve) => {
        const inicio = performance.now();
        const animar = (ahora) => {
          const avance = Math.min(1, (ahora - inicio) / duracion);
          this.aplicarEstilos(avance);
          if (avance < 1) requestAnimationFrame(animar);
          else resolve();
        };
        requestAnimationFrame(animar);
      });
    },

    repetirVuelo() {
      if (!this.ultimaCaja || !this.capa) return;
      this.aplicarEstilos(0);
      this.volar(this.ultimaCaja, this.ultimaCaja.centro);
    },

    // --- Formato de la ficha del terreno ------------------------------------
    // La normativa viene de la ficha y del CIP, y no siempre coinciden: manda
    // la del CIP por ser el documento oficial, y la otra queda de respaldo.

    metros(valor) {
      if (valor == null) return '—';
      return `${Math.round(valor).toLocaleString('es-CL')} m²`;
    },

    uf(valor) {
      if (valor == null) return '—';
      return Number(valor).toLocaleString('es-CL', { maximumFractionDigits: 2 });
    },

    densidad(capa) {
      const cip = capa.cip && capa.cip.densidad_maxima_hab_ha;
      const ficha = capa.normativa && capa.normativa.densidad_maxima;
      const valor = cip != null ? cip : ficha;
      return valor != null ? `${Math.round(valor).toLocaleString('es-CL')} hab/ha` : '—';
    },

    altura(capa) {
      const n = capa.normativa || {};
      const cip = capa.cip && capa.cip.altura_maxima_m;
      const partes = [];
      if (cip != null) partes.push(`${cip} m`);
      else if (n.altura_maxima) partes.push(n.altura_maxima);
      return partes.length ? partes.join(' · ') : '—';
    },

    // Cuántos m² se pueden construir: superficie por el coeficiente de
    // constructibilidad. Es la pregunta que de verdad importa.
    construccion(capa) {
      const n = capa.normativa || {};
      const coef = (capa.cip && capa.cip.constructibilidad_coef_max) != null
        ? capa.cip.constructibilidad_coef_max
        : (typeof n.constructibilidad === 'number' ? n.constructibilidad : null);
      if (coef == null) return typeof n.constructibilidad === 'string' ? n.constructibilidad : '—';
      if (capa.superficie_m2 == null) return `coef. ${coef}`;
      const edificable = Math.round(capa.superficie_m2 * coef);
      return `${edificable.toLocaleString('es-CL')} m² (coef. ${coef})`;
    },

    zonificacion(capa) {
      const cip = capa.cip || {};
      const n = capa.normativa || {};
      return cip.zona_codigo || n.zonificacion || '—';
    },

    usos(capa) {
      return (capa.cip && capa.cip.usos_permitidos) || [];
    },

    // --- Avisos -------------------------------------------------------------
    avisar(texto, tipo = 'info') {
      this.mensaje = texto;
      this.tipoMensaje = tipo;
    },

    fallar(texto) {
      this.avisar(texto || 'Algo salió mal.', 'error');
    },
  },
};
</script>
